/**
 * Places API (New) — nearby gas stations around a school / depot pin.
 *
 * Used by Premium/Enterprise "Ask Zana to find stations". Stores only place_id
 * + display name long-term (Google forbids keeping Places lat/lng beyond ~30d).
 */

export type NearbyFuelPlace = {
  placeId: string;
  name: string;
};

const SEARCH_RADIUS_M = 5000;
const MAX_STATIONS = 10;

type NearbyJson = {
  places?: Array<{
    id?: string;
    displayName?: { text?: string };
  }>;
  error?: { message?: string; status?: string };
};

export async function fetchNearbyFuelStations(
  apiKey: string,
  lat: number,
  lng: number,
): Promise<{ ok: true; places: NearbyFuelPlace[] } | { ok: false; message: string }> {
  if (!apiKey) {
    return { ok: false, message: "Maps key missing — set REACT_APP_GOOGLE_MAPS_API_KEY." };
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, message: "School / depot location is not set yet." };
  }

  try {
    const res = await fetch("https://places.googleapis.com/v1/places:searchNearby", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey,
        "X-Goog-FieldMask": "places.id,places.displayName",
      },
      body: JSON.stringify({
        includedTypes: ["gas_station"],
        maxResultCount: MAX_STATIONS,
        locationRestriction: {
          circle: {
            center: { latitude: lat, longitude: lng },
            radius: SEARCH_RADIUS_M,
          },
        },
      }),
    });
    const data = (await res.json()) as NearbyJson;
    if (!res.ok) {
      const detail = data.error?.message || `Places ${res.status}`;
      if (res.status === 403) {
        return {
          ok: false,
          message: "Places search is not authorised for this key — enable Places API (New).",
        };
      }
      return { ok: false, message: detail };
    }

    const places: NearbyFuelPlace[] = [];
    for (const p of data.places || []) {
      const placeId = String(p.id || "").trim();
      const name = String(p.displayName?.text || "").trim();
      if (!placeId || !name) continue;
      places.push({ placeId, name });
    }
    if (!places.length) {
      return {
        ok: false,
        message: "No petrol stations found within 5 km of the school pin. Add stations by hand.",
      };
    }
    return { ok: true, places };
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : "Could not reach Google Places.",
    };
  }
}

export const NEARBY_FUEL_STATION_TARGET = MAX_STATIONS;

/** School-picked favourites kept even when nearby refresh changes. */
export const PRIMARY_FUEL_STATION_LIMIT = 5;
